/** Login / Register / Reset — a single elegant surface (M10). */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, Brain, KeyRound, Loader2, Mail, Sparkles, User as UserIcon } from "lucide-react";

import {
  confirmReset, exchangeEmergentSession, login as apiLogin,
  register as apiRegister, requestReset, resendVerification, verifyEmail,
} from "@/entities/auth";
import type { Tokens } from "@/entities/auth/api";
import { useAuthStore } from "@/entities/auth/store";
import { setAuthToken } from "@/shared/api/http";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { OrkexraLogo } from "@/shared/ui/OrkexraLogo";
import { rememberGrowthReferral } from "@/entities/growth-network";

const AUTH_LANDING = "/home";
const ONBOARDING_STORAGE_KEY = "mos.onboarding.draft.v1";
const OAUTH_RETURN_TO_KEY = "mos.auth.oauth-return-to.v1";

function authDestination(params: URLSearchParams): string {
  const requested = params.get("returnTo");
  if (!requested || !requested.startsWith("/") || requested.startsWith("//")) return AUTH_LANDING;
  return requested;
}

function rememberOAuthDestination(destination: string): void {
  try {
    if (destination === AUTH_LANDING) window.sessionStorage.removeItem(OAUTH_RETURN_TO_KEY);
    else window.sessionStorage.setItem(OAUTH_RETURN_TO_KEY, destination);
  } catch { /* sessionStorage may be unavailable */ }
}

function consumeOAuthDestination(): string {
  try {
    const requested = window.sessionStorage.getItem(OAUTH_RETURN_TO_KEY);
    window.sessionStorage.removeItem(OAUTH_RETURN_TO_KEY);
    if (!requested) return AUTH_LANDING;
    return authDestination(new URLSearchParams({ returnTo: requested }));
  } catch {
    return AUTH_LANDING;
  }
}

type OnboardingDraft = {
  businessName?: string;
  industry?: string;
  website?: string;
};

/** Peek at the onboarding draft (if any) without disturbing it. */
function useOnboardingDraft(): OnboardingDraft | null {
  return useMemo<OnboardingDraft | null>(() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(ONBOARDING_STORAGE_KEY) : null;
      if (!raw) return null;
      const parsed = JSON.parse(raw) as OnboardingDraft;
      if (!parsed.businessName) return null;
      return parsed;
    } catch { return null; }
  }, []);
}

/** Aurora glow layer matching the marketing pages — welds the auth "room" into the same visual world. */
function AuthAurora(): JSX.Element {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute -left-40 -top-40 h-[600px] w-[600px] rounded-full opacity-50 blur-3xl"
           style={{ background: "radial-gradient(circle, rgba(229,180,84,0.12), transparent 60%)" }} />
      <div className="absolute -right-40 top-1/3 h-[520px] w-[520px] rounded-full opacity-40 blur-3xl"
           style={{ background: "radial-gradient(circle, rgba(74,222,128,0.08), transparent 60%)" }} />
      <svg className="absolute inset-0 h-full w-full opacity-[0.035]">
        <filter id="auth-n"><feTurbulence baseFrequency="0.9" numOctaves="2" /></filter>
        <rect width="100%" height="100%" filter="url(#auth-n)" />
      </svg>
    </div>
  );
}

export default function AuthPage(): JSX.Element {
  const [params] = useSearchParams();
  const fromOnboarding = params.get("from") === "onboarding";
  const referralCode = params.get("ref");
  const [tab, setTab] = useState<"login" | "register">(fromOnboarding || Boolean(referralCode) || params.get("mode") === "register" ? "register" : "login");
  const draft = useOnboardingDraft();
  const navigate = useNavigate();
  const verifyLinkToken = params.get("verify_token");
  const verifyLinkStarted = useRef(false);
  const verifyFromLink = useMutation({
    mutationFn: (token: string) => verifyEmail(token),
    onSuccess: () => {
      toast.success("Email verified. You can sign in now.");
      navigate("/login", { replace: true });
    },
    onError: (e: Error) => toast.error(e.message || "We couldn't verify that email link. It may have expired."),
  });

  useEffect(() => { if (referralCode) rememberGrowthReferral(referralCode); }, [referralCode]);
  useEffect(() => {
    if (verifyLinkToken && !verifyLinkStarted.current) {
      verifyLinkStarted.current = true;
      verifyFromLink.mutate(verifyLinkToken);
    }
  }, [verifyLinkToken]);

  return (
    <div data-testid="auth-page" className="relative min-h-screen bg-background text-[var(--md-ivory-0)]">
      <AuthAurora />
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col items-center justify-center px-6 py-12">
        <Link to="/welcome" className="mb-6 inline-flex items-center gap-3" data-testid="auth-brand-link">
          <OrkexraLogo variant="wordmark" size={30} />
        </Link>
        <div className="mb-6 akinex-eyebrow" data-testid="auth-attribution">
          Powered by Akinex&trade; Adaptive Intelligence
        </div>

        {fromOnboarding && draft && (
          <div
            data-testid="auth-onboarding-banner"
            className="mb-6 flex w-full max-w-4xl items-start gap-3 rounded-xl border border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/[0.06] p-4"
          >
            <span className="mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg border border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/10">
              <Brain className="h-4 w-4 text-[var(--md-amber)]" />
            </span>
            <div className="flex-1 text-sm">
              <p className="font-semibold text-[var(--md-ivory-0)]">
                Your Brand Brain for <span className="text-[var(--md-amber)]">{draft.businessName}</span> is ready.
              </p>
              <p className="mt-0.5 text-xs text-[var(--md-fog)]">
                Create your workspace to save it, generate your first campaign, and invite your team. Takes about a minute.
              </p>
            </div>
            <Sparkles className="hidden h-4 w-4 flex-shrink-0 text-[var(--md-amber)] sm:block" />
          </div>
        )}

        {referralCode && (
          <div data-testid="auth-referral-banner" className="mb-6 w-full max-w-4xl rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4 text-sm">
            You were invited to build your business workspace with Orkexra. Create your account to continue.
          </div>
        )}

        <div className="grid w-full max-w-4xl gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="hidden flex-col justify-center gap-5 md:flex">
            <h1 className="font-display italic tracking-tight text-[var(--md-ivory-0)] text-4xl leading-[1.05] lg:text-5xl">
              {fromOnboarding
                ? <>One step away from your <span className="brand-gradient-text">first campaign</span>.</>
                : <>Your marketing department,<br />in one <span className="brand-gradient-text">command center</span>.</>}
            </h1>
            <p className="text-sm text-[var(--md-fog)] leading-relaxed">
              {fromOnboarding
                ? "Save the Brand Brain you just built, generate content in your voice, and ship campaigns across every channel — all from a single workspace."
                : "Brand Brain, campaigns, content, media, publishing, and executive intelligence — designed to run like one thoughtful team."}
            </p>
            <div className="flex flex-wrap gap-1 pt-2">
              {["Brand Brain", "Campaigns", "Media", "Publishing", "Intelligence"].map((t) => (
                <span key={t} className="rounded-full border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[var(--md-fog)]">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <Card className="md-panel--glass border-[color:var(--md-amber)]/25">
            <Tabs value={tab} onValueChange={(v) => setTab(v as "login" | "register")}>
              <CardHeader className="pb-2">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger data-testid="tab-login" value="login">Sign in</TabsTrigger>
                  <TabsTrigger data-testid="tab-register" value="register">Create account</TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent>
                <TabsContent value="login"><LoginForm /></TabsContent>
                <TabsContent value="register"><RegisterForm switchToLogin={() => setTab("login")} /></TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </div>
    </div>
  );
}

function GoogleButton(): JSX.Element {
  const [params] = useSearchParams();
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    rememberOAuthDestination(authDestination(params));
    const redirectUrl = window.location.origin + AUTH_LANDING;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button
      type="button" variant="outline" onClick={start}
      data-testid="google-oauth-button" className="w-full">
      <span className="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 via-red-500 to-yellow-500 text-[10px] font-bold text-white">G</span>
      Continue with Google
    </Button>
  );
}

function LoginForm(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resetToken = params.get("reset_token");
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgot, setForgot] = useState(Boolean(resetToken));

  const login = useMutation({
    mutationFn: () => apiLogin(email, password),
    onSuccess: (t) => {
      setAuth(t);
      setAuthToken(t.access_token);
      // Patch 24 · never fall back to email as a greeting.
      const greetName = (t.user.preferred_name?.trim() || t.user.name?.split(/\s+/)[0] || "friend");
      toast.success(`Welcome back, ${greetName}. Your workspace is ready.`);
      navigate(authDestination(params), { replace: true });
    },
    onError: (e: Error) =>
      toast.error(e.message || "We couldn't sign you in right now. Please try again in a moment."),
  });

  if (forgot) return <ForgotForm onBack={() => setForgot(false)} initialToken={resetToken} />;

  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); login.mutate(); }}>
      <GoogleButton />
      <Divider label="or continue with email" />
      <div className="space-y-1">
        <Label htmlFor="l-email">Email</Label>
        <Input id="l-email" type="email" required autoComplete="email"
               data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="l-password">Password</Label>
        <Input id="l-password" type="password" required autoComplete="current-password"
               data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <Button
        type="submit"
        data-testid="login-submit"
        disabled={login.isPending || !email || !password}
        className="w-full">
        {login.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
        Sign in
      </Button>
      <button type="button" onClick={() => setForgot(true)}
              data-testid="forgot-password-link"
              className="mt-1 text-xs text-muted-foreground hover:text-foreground">
        Forgot your password?
      </button>
    </form>
  );
}

function RegisterForm({ switchToLogin }: { switchToLogin: () => void }): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [verifyToken, setVerifyToken] = useState<string | null>(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [deliveryAvailable, setDeliveryAvailable] = useState(false);

  const reg = useMutation({
    mutationFn: () => apiRegister({ email, password, name }),
    onSuccess: (r) => {
      setVerifyToken(r.verify_token);
      setRegistrationComplete(true);
      setDeliveryAvailable(r.delivery_available);
      if (r.delivery_available) toast.success("Your account is ready. Check your email to verify your address.");
      else if (r.verify_token) toast.success("Your account is ready. Verify your email to sign in.");
      else toast.error("Your account was created, but verification email delivery is temporarily unavailable.");
    },
    onError: (e: Error) =>
      toast.error(e.message || "We couldn't create the account right now. Please try again in a moment."),
  });

  const verify = useMutation({
    mutationFn: (t: string) => verifyEmail(t),
    onSuccess: () => { toast.success("You're verified. Welcome to Orkexra."); switchToLogin(); },
    onError: (e: Error) => toast.error(e.message || "We couldn't verify your email. Please try the link again."),
  });

  const resend = useMutation({
    mutationFn: () => resendVerification(email),
    onSuccess: (r) => {
      if (r.verify_token) setVerifyToken(r.verify_token);
      setDeliveryAvailable(r.delivery_available);
      if (r.delivery_available) {
        toast.success("If this account still needs verification, a new link will arrive shortly.");
      } else {
        toast.error("Verification email delivery is temporarily unavailable. Please try again shortly.");
      }
    },
    onError: (e: Error) =>
      toast.error(e.message || "We couldn't resend the verification email. Please try again in a moment."),
  });

  if (registrationComplete) {
    return (
      <div className="space-y-4" data-testid="register-check-email">
        <div className="rounded-lg border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/[0.05] p-4">
          <p className="flex items-center gap-2 font-semibold text-[var(--md-ivory-0)]">
            <Mail className="h-4 w-4 text-[var(--md-amber)]" /> Check your email
          </p>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            We created your Orkexra account for <strong>{email}</strong>. Open the verification link in your email to finish setup and sign in.
          </p>
          {!deliveryAvailable && !verifyToken ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Delivery is temporarily unavailable. You can retry below without creating another account.
            </p>
          ) : null}
        </div>

        <Button type="button" variant="outline" className="w-full"
                onClick={() => resend.mutate()} disabled={resend.isPending}
                data-testid="resend-verification-button">
          {resend.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
          Resend verification email
        </Button>

        {verifyToken ? (
          <div data-testid="register-verify-panel" className="rounded-md border border-emerald-500/40 bg-emerald-50/50 p-3 text-xs dark:bg-emerald-950/20">
            <p className="text-muted-foreground">Preview/local verification token is available.</p>
            <Button type="button" size="sm" className="mt-2" onClick={() => verify.mutate(verifyToken)}
                    disabled={verify.isPending} data-testid="verify-now-button">
              Verify email now <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        ) : null}

        <Button type="button" variant="ghost" className="w-full" data-testid="register-use-different-email"
                onClick={() => { setRegistrationComplete(false); setVerifyToken(null); setDeliveryAvailable(false); }}>
          Use a different email
        </Button>
      </div>
    );
  }

  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); reg.mutate(); }}>
      <GoogleButton />
      <Divider label="or create with email" />
      <div className="space-y-1">
        <Label htmlFor="r-name">Full name</Label>
        <Input id="r-name" required autoComplete="name" data-testid="register-name"
               value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="r-email">Email</Label>
        <Input id="r-email" type="email" required autoComplete="email" data-testid="register-email"
               value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="r-password">Password (8+ chars)</Label>
        <Input id="r-password" type="password" required autoComplete="new-password" minLength={8}
               data-testid="register-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <Button type="submit" data-testid="register-submit"
              disabled={reg.isPending || !email || !password || !name} className="w-full">
        {reg.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserIcon className="mr-2 h-4 w-4" />}
        Create account
      </Button>
    </form>
  );
}

function ForgotForm({ onBack, initialToken = null }: { onBack: () => void; initialToken?: string | null }): JSX.Element {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState<string | null>(initialToken);
  const [newPassword, setNewPassword] = useState("");

  const req = useMutation({
    mutationFn: () => requestReset(email),
    onSuccess: (r) => {
      setToken(r.reset_token);
      if (r.reset_token) toast.success("Preview reset token is ready.");
      else if (r.delivery_available) toast.success("If an account exists for that email, a reset link will arrive shortly.");
      else toast.error("Password-reset email delivery is currently unavailable. Please contact support.");
    },
    onError: (e: Error) =>
      toast.error(e.message || "We couldn't send that reset link. Please try again in a moment."),
  });
  const conf = useMutation({
    mutationFn: () => confirmReset(token as string, newPassword),
    onSuccess: () => { toast.success("Password updated. You can sign in now."); onBack(); },
    onError: (e: Error) =>
      toast.error(e.message || "We couldn't update your password. The reset link may have expired — please request a new one."),
  });

  return (
    <div className="space-y-3">
      {!token ? (
        <form onSubmit={(e) => { e.preventDefault(); req.mutate(); }} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="f-email">Email</Label>
            <Input id="f-email" type="email" required data-testid="forgot-email"
                   value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <Button type="submit" className="w-full" data-testid="forgot-submit" disabled={req.isPending || !email}>
            {req.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
            Send reset link
          </Button>
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); conf.mutate(); }} className="space-y-3">
          <p className="text-xs text-muted-foreground" data-testid="forgot-preview-note">
            Preview environment — reset link resolved instantly.
          </p>
          <div className="space-y-1">
            <Label htmlFor="np">New password</Label>
            <Input id="np" type="password" required minLength={8} data-testid="forgot-new-password"
                   value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </div>
          <Button type="submit" className="w-full" data-testid="forgot-confirm"
                  disabled={conf.isPending || !newPassword}>
            Update password
          </Button>
        </form>
      )}
      <Button type="button" variant="ghost" onClick={onBack} className="w-full" data-testid="forgot-back">
        Back to sign in
      </Button>
    </div>
  );
}

function Divider({ label }: { label: string }): JSX.Element {
  return (
    <div className="my-1 flex items-center gap-2">
      <div className="h-px flex-1 bg-border/60" />
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="h-px flex-1 bg-border/60" />
    </div>
  );
}

/** OAuth callback landing — Emergent redirects here with `#session_id=…`.
 *
 * Patch 37.4.2 — Google OAuth Callback Stabilization.
 *
 * Contract this component enforces:
 *
 *   1. The exchange for a given ``session_id`` runs AT MOST ONCE per
 *      SPA session, no matter how many times the component mounts /
 *      re-renders / is StrictMode-double-invoked / is remounted after
 *      HMR. Guarded by a module-level ``inFlight`` map keyed by the
 *      session id itself.
 *   2. On success, ``setAuth`` fires once, one welcome toast surfaces
 *      (deduped by a stable Sonner id), and navigation to the auth
 *      landing uses history REPLACEMENT so ``/auth/callback`` never
 *      remains in the back/forward stack. If react-router's replace
 *      doesn't take effect within a short window (parent re-mounting,
 *      concurrent-mode quirk), a hard ``window.location.replace`` is
 *      used as the belt-and-braces fallback.
 *   3. A missing / malformed ``session_id`` never spins forever — the
 *      component renders a founder-safe error card with a link back
 *      to ``/login``. If the visitor already has a valid session
 *      (e.g. they refreshed the callback URL after a successful
 *      exchange), we silently forward them to the auth landing
 *      instead of surfacing an error.
 *   4. The hash fragment is stripped from the URL BEFORE the async
 *      exchange begins, so a reload while the exchange is in flight
 *      cannot replay the callback with a stale session id.
 *   5. Email / password login (``LoginForm`` above) is untouched.
 */

/** Module-level exchange registry. Keys are session ids; values track
 *  the in-flight or completed exchange outcome. Persisting across
 *  component instances is the point — it survives StrictMode
 *  double-mount, HMR-driven remount, and any router-driven remount. */
type OAuthExchangeOutcome =
  | { status: "in-flight"; promise: Promise<Tokens> }
  | { status: "resolved"; tokens: Tokens }
  | { status: "failed"; error: Error };
const OAUTH_EXCHANGE_REGISTRY = new Map<string, OAuthExchangeOutcome>();

function beginOrJoinExchange(sessionId: string): Promise<Tokens> {
  const existing = OAUTH_EXCHANGE_REGISTRY.get(sessionId);
  if (existing) {
    if (existing.status === "in-flight") return existing.promise;
    if (existing.status === "resolved") return Promise.resolve(existing.tokens);
    if (existing.status === "failed") return Promise.reject(existing.error);
  }
  const promise = exchangeEmergentSession(sessionId).then(
    (tokens) => {
      OAUTH_EXCHANGE_REGISTRY.set(sessionId, { status: "resolved", tokens });
      return tokens;
    },
    (error: Error) => {
      OAUTH_EXCHANGE_REGISTRY.set(sessionId, { status: "failed", error });
      throw error;
    },
  );
  OAUTH_EXCHANGE_REGISTRY.set(sessionId, { status: "in-flight", promise });
  return promise;
}

/** Extract ``session_id`` from either the URL hash fragment or the
 *  ``returnTo`` query parameter (defence-in-depth: if some redirect
 *  chain encodes the hash into the query, we still recover). */
function extractSessionId(): string | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  const hashMatch = hash.match(/session_id=([^&]+)/);
  if (hashMatch) return decodeURIComponent(hashMatch[1]);
  try {
    const returnTo = new URLSearchParams(window.location.search).get("returnTo");
    if (returnTo) {
      const nested = returnTo.match(/session_id=([^&]+)/);
      if (nested) return decodeURIComponent(nested[1]);
    }
  } catch { /* noop */ }
  return null;
}

function stripCallbackFragment(): void {
  try {
    window.history.replaceState(null, "", "/auth/callback");
  } catch { /* noop */ }
}

type CallbackPhase = "exchanging" | "error";

export function AuthCallbackPage(): JSX.Element {
  const nav = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [phase, setPhase] = useState<CallbackPhase>("exchanging");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    // Per-mount guard (StrictMode double-invoke bail).
    if (startedRef.current) return;
    startedRef.current = true;

    const sessionId = extractSessionId();

    // No session id in the URL. Two legitimate cases:
    //   a) the visitor already exchanged in this tab and refreshed —
    //      they still hold a valid access token, so quietly land them
    //      on Home (no toast, no re-exchange);
    //   b) the visitor arrived here directly / with a broken redirect —
    //      show a founder-safe error card.
    if (!sessionId) {
      if (accessToken) {
        nav(AUTH_LANDING, { replace: true });
        return;
      }
      setErrorMessage("We couldn't complete your sign-in. Please try again.");
      setPhase("error");
      return;
    }

    // Strip the fragment before the async work — a reload mid-flight
    // must not be able to re-fire the exchange with a stale id.
    stripCallbackFragment();

    beginOrJoinExchange(sessionId)
      .then((tokens) => {
        setAuth(tokens);
        setAuthToken(tokens.access_token);
        // Deduped by Sonner id — even if a re-mount races us we still
        // surface exactly one welcome toast per user, per SPA session.
        const greetName =
          tokens.user.preferred_name?.trim()
          || tokens.user.name?.split(/\s+/)[0]
          || "friend";
        toast.success(`Welcome, ${greetName}`, {
          id: `oauth-welcome-${tokens.user.id}`,
        });
        const destination = consumeOAuthDestination();
        nav(destination, { replace: true });
        // Belt-and-braces: if react-router's replace doesn't take
        // effect within a short window (parent re-render, concurrent
        // mode quirk, guard race), force a hard replace so the
        // ``/auth/callback`` URL never lingers in the address bar.
        window.setTimeout(() => {
          if (window.location.pathname.startsWith("/auth/callback")) {
            window.location.replace(destination);
          }
        }, 400);
      })
      .catch((err: Error) => {
        // If a prior mount already completed the exchange (or the
        // visitor arrived with a valid session cached in the store),
        // this rejection is benign — treat it as success.
        if (useAuthStore.getState().accessToken) {
          nav(AUTH_LANDING, { replace: true });
          return;
        }
        setErrorMessage(err.message || "Sign-in didn't complete. Please try again.");
        setPhase("error");
      });
    // ``nav``/``setAuth``/``accessToken`` are stable identities from
    // hooks. Empty dep list would work too — the ref guard makes the
    // effect single-fire regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="auth-callback"
      data-phase={phase}
      className="mx-auto flex min-h-screen max-w-md items-center justify-center px-6"
    >
      {phase === "error" ? (
        <div
          data-testid="auth-callback-error"
          className="rounded-md border border-rose-500/40 bg-rose-50/50 p-4 text-sm dark:bg-rose-950/20"
        >
          <p className="font-medium">Sign-in didn't complete</p>
          <p
            data-testid="auth-callback-error-message"
            className="mt-1 text-xs text-muted-foreground"
          >
            {errorMessage}
          </p>
          <Link
            to="/login"
            data-testid="auth-callback-back-to-login"
            className="mt-2 inline-block text-xs underline"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <div data-testid="auth-callback-loader" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Signing you in…
        </div>
      )}
    </div>
  );
}
